import { getTextDelta } from '@databases/react/useTextDelta';
import { DefaultRootState } from '@databases/redux';
import { DatabaseFieldType } from '@databases/types/database';
import { Database } from '@databases/types/database';
import { DatabaseField, DatabaseEntry, DatabaseValue } from '@databases/types/database';
import { isBoolean, isEmpty, isString } from 'lodash-es';

import {
  MultiValueChangeType,
  MultiValueFieldType,
  ValueChange,
} from '../database/changesets';
import {
  getContentLabels,
  getReferencedLabelContentRefs,
  PageLabelsDelta,
} from '../database/field-types/pageLabels';
import { applyPageLabels } from '../database/field-types/pageLabels';
import {
  isExistingPageLinkValue,
  normalizePageLinkValue,
} from '../database/field-types/pageLink';
import {
  DEFAULT_PAGE_STATUS_COLOR,
  applyPageStatus,
  getContentStatus,
  getReferencedStatusContentRefs,
} from '../database/field-types/pageStatus';
import { getConvertedDatabaseValue } from '../database/utils';
import { SimpleStore } from '../redux/redux';
import { normalizeText } from '../utils';

import { getLabelsDelta } from './labels-delta';

const isMultiValueFieldType = (
  value?: DatabaseValue,
  type?: DatabaseFieldType
): value is DatabaseValue<MultiValueFieldType | DatabaseFieldType.HYPERLINK> => {
  return value?.type === type;
};

type MappingPropType<
  T extends MultiValueFieldType | DatabaseFieldType.HYPERLINK =
    | MultiValueFieldType
    | DatabaseFieldType.HYPERLINK
> = keyof DatabaseValue<T>['value'][0];

/**
 * Append not existing values when multi select is enabled and appendValue is true
 * Otherwise replace the value
 * */
const appendOrReplaceMultiFieldValue = <
  T extends MultiValueFieldType | DatabaseFieldType.HYPERLINK
>(
  {
    field,
    databaseValue,
    previousDatabaseValue,
  }: {
    field: DatabaseField;
    databaseValue?: DatabaseValue;
    previousDatabaseValue: DatabaseValue;
  },
  appendValue: boolean,
  mappingProp?: MappingPropType<T> | string
): ValueChange[] => {
  const isMultiValue = isMultiValueFieldType(databaseValue, field.type);
  if (!isMultiValue) {
    return [];
  }
  const values = Array.isArray(databaseValue.value)
    ? databaseValue.value
    : [databaseValue.value];
  const previousValues = Array.isArray(previousDatabaseValue.value)
    ? previousDatabaseValue.value
    : [previousDatabaseValue.value];

  const newValues = values.filter((value) => {
    // If the value is a string and all previous values are also strings
    if (isString(value) && previousValues.every((v) => isString(v))) {
      return !(previousValues as string[]).includes(value);
    }

    // If the value is a boolean and all previous values are also booleans
    if (isBoolean(value) && previousValues.every((v) => isBoolean(v))) {
      return !(previousValues as boolean[]).includes(value);
    }

    // If the value is not a string and the mapping property exists in the value
    if (!isString(value) && !isBoolean(value) && mappingProp && mappingProp in value) {
      const previousPropValues = previousValues
        .filter(
          (previousValue) =>
            previousValue &&
            !isString(previousValue) &&
            !isBoolean(previousValue) &&
            mappingProp in previousValue
        )
        .map((previousValue) => previousValue?.[mappingProp as MappingPropType]);

      return !previousPropValues.includes(value[mappingProp as MappingPropType]);
    }

    return false;
  });

  return field.settings.multi && appendValue
    ? newValues.map(
        (v) =>
          ({
            type: field.type,
            delta: {
              type: MultiValueChangeType.APPEND,
              value: v,
            },
          } as ValueChange)
      )
    : ([
        {
          type: field.type,
          delta: {
            type: MultiValueChangeType.SET_TO,
            value: values,
          },
        },
      ] as ValueChange[]);
};

const appendOrReplacePageLabels = (
  {
    entry,
    field,
    database,
    text,
  }: {
    database: Database;
    field: DatabaseField;
    entry: DatabaseEntry;
    text: string | undefined;
  },
  { getState, dispatch }: SimpleStore<DefaultRootState>,
  appendValue: boolean
) => {
  getReferencedLabelContentRefs({ database, field, entry }, getState())?.map(
    (contentRef) => {
      const existingLabels = getContentLabels(contentRef, {}, getState()) ?? [];
      const normalizedLabelsArray = normalizeText(text)?.split(' ') || [];
      const newLabels = isEmpty(text) ? [] : normalizedLabelsArray;
      const labelsToRemove = existingLabels.filter((label) => !newLabels.includes(label));
      const deltas: PageLabelsDelta = { add: [], remove: [] };

      newLabels.forEach((label) => {
        const delta = getLabelsDelta({
          action: 'select-option',
          option: { value: label, label },
        });
        if (delta) {
          deltas.add.push(...delta.add);
        }
      });

      if (!appendValue) {
        labelsToRemove.forEach((label) => {
          const delta = getLabelsDelta({
            action: 'deselect-option',
            option: { value: label, label },
          });
          if (delta) {
            deltas.remove.push(...delta.remove);
          }
        });
      }

      if (field.settings.allowEdit) {
        applyPageLabels(contentRef, deltas, { getState, dispatch });
      }
    }
  );
};

const replacePageStatus = (
  {
    entry,
    field,
    database,
    value,
    text,
  }: {
    database: Database;
    field: DatabaseField;
    entry: DatabaseEntry;
    value: DatabaseValue | undefined;
    text: string | undefined;
  },
  { getState, dispatch }: SimpleStore<DefaultRootState>
) => {
  const newValue = (value as DatabaseValue<DatabaseFieldType.PAGE_STATUS>)?.value;
  getReferencedStatusContentRefs({ database, field, entry }, getState())?.map(
    (contentRef) => {
      const existingStatus =
        newValue && getContentStatus(contentRef, newValue, getState());
      if (text && field.settings.allowEdit && existingStatus?.name !== text) {
        const newStatus = {
          name: text,
          color: DEFAULT_PAGE_STATUS_COLOR,
        };
        applyPageStatus(contentRef, newStatus, { getState, dispatch });
      }
    }
  );
};

/**
 * Returns the value change (delta) for the pasted value for the given field
 * or null if the value should not be applied or the field is read only.
 * For some fields, the value change is applied to the referenced content.
 **/
export function getOrApplyValueChange(
  {
    entry,
    field,
    value,
    database,
    text,
  }: {
    database: Database;
    field: DatabaseField;
    entry: DatabaseEntry;
    value: DatabaseValue | undefined;
    text: string | undefined;
  },
  { getState, dispatch }: SimpleStore<DefaultRootState>,
  appendValue: boolean
): ValueChange[] | null {
  const previousDatabaseValue = getConvertedDatabaseValue(
    database,
    field,
    entry,
    getState()
  );
  switch (field.type) {
    // read only fields
    case DatabaseFieldType.JIRA_ISSUE_DETAILS:
    case DatabaseFieldType.ENTRY_DETAILS:
    case DatabaseFieldType.BACKLINK:
    case DatabaseFieldType.PAGE_DETAILS: {
      return null;
    }

    // fields which has to be applied to referenced content
    case DatabaseFieldType.PAGE_LABELS: {
      appendOrReplacePageLabels(
        { entry, field, database, text },
        { getState, dispatch },
        appendValue
      );
      return null;
    }
    case DatabaseFieldType.PAGE_STATUS: {
      replacePageStatus({ entry, field, value, database, text }, { getState, dispatch });
      return null;
    }

    // single value fields
    case DatabaseFieldType.TEXT:
      return [
        {
          type: field.type,
          delta: getTextDelta(
            (previousDatabaseValue as DatabaseValue<DatabaseFieldType.TEXT>)?.value ?? '',
            (value as DatabaseValue<DatabaseFieldType.TEXT>)?.value ?? ''
          ),
        },
      ];
    case DatabaseFieldType.NUMBER: {
      return [
        {
          type: field.type,
          value: (value as DatabaseValue<DatabaseFieldType.NUMBER>)?.value,
        },
      ];
    }
    case DatabaseFieldType.CHECKBOX: {
      return [
        {
          type: field.type,
          value: (value as DatabaseValue<DatabaseFieldType.CHECKBOX>)?.value,
        },
      ];
    }
    case DatabaseFieldType.DATE: {
      return [
        {
          type: field.type,
          value: (value as DatabaseValue<DatabaseFieldType.DATE>)?.value,
        },
      ];
    }
    case DatabaseFieldType.ATTACHMENT: {
      return [
        {
          type: field.type,
          value: (value as DatabaseValue<DatabaseFieldType.ATTACHMENT>)?.value,
        },
      ];
    }

    // multi value fields
    case DatabaseFieldType.HYPERLINK: {
      return appendOrReplaceMultiFieldValue(
        {
          field,
          databaseValue: value,
          previousDatabaseValue,
        },
        appendValue,
        'url'
      );
    }
    case DatabaseFieldType.ENTRY_LINK: {
      return appendOrReplaceMultiFieldValue(
        {
          field,
          databaseValue: value,
          previousDatabaseValue,
        },
        appendValue,
        'entryId'
      );
    }
    case DatabaseFieldType.USER: {
      return appendOrReplaceMultiFieldValue(
        {
          field,
          databaseValue: value,
          previousDatabaseValue,
        },
        appendValue,
        'accountId'
      );
    }
    case DatabaseFieldType.JIRA_ISSUE: {
      return appendOrReplaceMultiFieldValue(
        {
          field,
          databaseValue: value,
          previousDatabaseValue,
        },
        appendValue,
        'key'
      );
    }
    case DatabaseFieldType.SELECT: {
      return appendOrReplaceMultiFieldValue(
        {
          field,
          databaseValue: value,
          previousDatabaseValue,
        },
        appendValue
      );
    }
    case DatabaseFieldType.PAGE_LINK: {
      const previousValue = (
        previousDatabaseValue as DatabaseValue<DatabaseFieldType.PAGE_LINK>
      ).value;
      const newValue = (value as DatabaseValue<DatabaseFieldType.PAGE_LINK>)?.value;
      const newContentRefs = normalizePageLinkValue(newValue, getState()).filter((v) =>
        isExistingPageLinkValue(v)
      );
      const previousContentRefs = normalizePageLinkValue(
        previousValue,
        getState()
      ).filter((v) => isExistingPageLinkValue(v));
      return appendOrReplaceMultiFieldValue(
        {
          field,
          databaseValue: {
            value: newContentRefs,
            type: field.type,
          },
          previousDatabaseValue: {
            value: previousContentRefs,
            type: field.type,
          },
        },
        appendValue,
        'contentId'
      );
    }
  }
}
